/**
 * Recharge automatiquement la page lorsqu'un `ChunkLoadError` se produit.
 *
 * Contexte :
 * Avec le code-splitting (React.lazy + import dynamique), le navigateur va
 * télécharger un chunk JS au moment où l'utilisateur navigue vers une route.
 * Si une nouvelle version du backoffice a été déployée entre-temps, l'ancien
 * hash de chunk n'existe plus → erreur "Loading chunk N failed" et écran blanc.
 *
 * Autre cas fréquent : le serveur renvoie index.html (fallback SPA) pour l'URL
 * du chunk → le navigateur exécute du HTML comme du JS et lève
 * SyntaxError: Unexpected token '<'.
 *
 * Mitigation :
 * - On détecte ce type d'erreur via window.onerror, unhandledrejection,
 *   ErrorBoundary React, et le catch des loaders lazy.
 * - On stocke un flag de session pour éviter une boucle infinie de reload.
 * - On signale une fois à Sentry (`chunk_load_recovery`) pour monitoring.
 * - On force un reload propre pour récupérer les nouveaux hashes.
 *
 * À importer une seule fois, le plus tôt possible (depuis index.tsx).
 */

import type { ComponentType } from 'react';

const RELOAD_FLAG_KEY = 'rintintin:chunk-reload-attempt';
export const RECOVERY_REPORT_FLAG_KEY = 'rintintin:chunk-recovery-reported';

// Accepte aussi les suffixes stack `:line:col` (ex. `/static/js/2205.abc.chunk.js:1:1`).
const STATIC_BUNDLE_RE = /\/static\/js\/[^\s?'"]+\.(?:js|mjs)/i;
const STATIC_CSS_RE = /\/static\/css\/[^\s?'"]+\.css/i;
const CHUNK_FILE_RE = /\.chunk\.(?:js|mjs|css)/i;

type LazyModule = { default: ComponentType<unknown> };
type LazyLoader = () => Promise<LazyModule>;

const mentionsStaticBundle = (text: string): boolean =>
  STATIC_BUNDLE_RE.test(text) || STATIC_CSS_RE.test(text) || CHUNK_FILE_RE.test(text);

const getResourceUrl = (target: EventTarget | null): string => {
  if (target instanceof HTMLScriptElement && target.src) return target.src;
  if (target instanceof HTMLLinkElement && target.href) return target.href;
  return '';
};

/**
 * Échec de chargement d’un `<script>` / `<link>` sous `/static/`
 * (CSS chunk 404, JS main manquant avant boot React, etc.).
 * L’event `error` n’a alors pas de `event.error` — seulement `target`.
 */
export const isStaticAssetResourceError = (event: Event): boolean => {
  const url = getResourceUrl(event.target);
  if (!url) return false;
  return mentionsStaticBundle(url);
};

const getErrorMessage = (error: unknown): string => {
  if (!error) return '';
  if (error instanceof Error) return error.message || '';
  if (typeof error === 'string') return error;
  return (error as { message?: string })?.message ?? '';
};

const getErrorStack = (error: unknown): string => {
  if (error instanceof Error && typeof error.stack === 'string') return error.stack;
  return '';
};

const isUnexpectedHtmlTokenSyntaxError = (error: unknown): boolean => {
  const name = error instanceof Error ? error.name : '';
  const message = getErrorMessage(error);
  const msg = message.toLowerCase();
  if (!(name === 'SyntaxError' || msg.includes('syntaxerror'))) {
    // Certains navigateurs ne mettent que le message
    if (!msg.includes('unexpected token')) return false;
  }
  return (
    msg.includes("unexpected token '<") ||
    msg.includes('unexpected token "<') ||
    /unexpected token\s*['"]?<\s*(!|\/|\?)/i.test(message)
  );
};

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;

  const message = getErrorMessage(error);
  const name = error instanceof Error ? error.name : '';

  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

/**
 * SyntaxError « Unexpected token '<' » pointant vers un bundle `/static/js` ou `.chunk.js`.
 * Volontairement strict hors du catch lazy (évite de masquer de vraies SyntaxError).
 */
export const isStaleChunkHtmlError = (error: unknown): boolean => {
  if (!isUnexpectedHtmlTokenSyntaxError(error)) return false;
  const haystack = `${getErrorMessage(error)}\n${getErrorStack(error)}`;
  return mentionsStaticBundle(haystack);
};

/**
 * Détecte le cas « chunk JS remplacé par du HTML » via ErrorEvent.
 * Fallback stack si `filename` / `target` absents (souvent mobile).
 */
export const isStaleChunkHtmlScriptError = (event: ErrorEvent): boolean => {
  const rawMessage =
    (event.error instanceof Error ? event.error.message : '') ||
    (typeof event.message === 'string' ? event.message : '');
  if (!isUnexpectedHtmlTokenSyntaxError(event.error ?? rawMessage)) {
    // event.message seul (string) peut suffire
    const msg = rawMessage.toLowerCase();
    const looksLikeHtmlParsedAsJs =
      msg.includes("unexpected token '<") ||
      msg.includes('unexpected token "<') ||
      /unexpected token\s*['"]?<\s*(!|\/|\?)/i.test(rawMessage);
    if (!looksLikeHtmlParsedAsJs) return false;
  }

  const scriptTarget = event.target;
  if (scriptTarget instanceof HTMLScriptElement && scriptTarget.src) {
    if (mentionsStaticBundle(scriptTarget.src)) return true;
  }

  const filename = typeof event.filename === 'string' ? event.filename : '';
  if (filename && mentionsStaticBundle(filename)) return true;

  if (isStaleChunkHtmlError(event.error)) return true;

  return false;
};

/** Handlers globaux / ErrorBoundary — strict (bundle path requis pour HTML-as-JS). */
export const shouldRecoverFromChunkFailure = (error: unknown): boolean =>
  isChunkLoadError(error) || isStaleChunkHtmlError(error);

/**
 * Catch d'un `import()` de route lazy : le contexte est déjà un chunk.
 * SyntaxError Unexpected token '<' y suffit (stack parfois vide sur mobile).
 */
export const shouldRecoverFromLazyImportFailure = (error: unknown): boolean => {
  if (shouldRecoverFromChunkFailure(error)) return true;
  return isUnexpectedHtmlTokenSyntaxError(error);
};

/**
 * Signal monitoring (1× / session) — tag Sentry `chunk_load_recovery`.
 * Ne remplace pas le captureException éventuel ; complète pour alerter / dashboard.
 */
export const reportChunkRecovery = (source: string): void => {
  try {
    if (sessionStorage.getItem(RECOVERY_REPORT_FLAG_KEY) === '1') return;
    sessionStorage.setItem(RECOVERY_REPORT_FLAG_KEY, '1');
  } catch {
    // sessionStorage indisponible : on tente quand même le report une fois
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const Sentry = require('@sentry/react') as {
      captureMessage?: (
        message: string,
        context?: { level?: string; tags?: Record<string, string> }
      ) => void;
    };
    Sentry.captureMessage?.('chunk_load_recovery', {
      level: 'warning',
      tags: {
        chunk_load_recovery: 'true',
        recovery_source: source,
      },
    });
  } catch {
    // Sentry absent / non initialisé — pas bloquant
  }
};

export const triggerChunkReload = (): boolean => {
  let alreadyTried = false;
  try {
    alreadyTried = sessionStorage.getItem(RELOAD_FLAG_KEY) === '1';
  } catch {
    // sessionStorage indisponible (mode privé strict) : on tente quand même
    // un reload, sans protection contre la boucle.
  }

  if (alreadyTried) {
    console.warn(
      '[ChunkErrorHandler] Nouveau ChunkLoadError après un reload. ' +
        'Probablement un vrai problème (réseau, CSP...). Pas de nouveau reload.'
    );
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
  } catch {
    // ignore
  }

  console.warn(
    '[ChunkErrorHandler] Chunk obsolète ou chargement échoué → reload automatique pour récupérer la nouvelle version.'
  );

  // Cache-bust : un simple reload() peut resservir un index.html stale
  // depuis le bfcache / un CDN edge. On force une navigation avec nonce.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_rtt_chunk', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return true;
};

/**
 * Mémoïse un import dynamique et, en cas d'échec chunk/HTML-as-JS :
 * - invalide le cache (évite promesse rejetée permanente)
 * - signale + reload une fois
 */
export const createRecoverableLazyLoader = (loader: LazyLoader): LazyLoader => {
  let cached: Promise<LazyModule> | null = null;
  return () => {
    if (!cached) {
      cached = loader().catch((err: unknown) => {
        cached = null;
        if (shouldRecoverFromLazyImportFailure(err)) {
          reportChunkRecovery('lazy_import');
          triggerChunkReload();
        }
        throw err;
      });
    }
    return cached;
  };
};

/**
 * Active la détection globale. Idempotent : un second appel ne fait rien.
 * Réinitialise aussi le flag de reload après 30s : si on est arrivé à charger
 * sans erreur pendant 30s, on considère que l'app fonctionne.
 */
export const installChunkErrorHandler = (): void => {
  if (typeof window === 'undefined') return;
  if ((window as unknown as { __rttChunkHandlerInstalled?: boolean }).__rttChunkHandlerInstalled) {
    return;
  }
  (window as unknown as { __rttChunkHandlerInstalled?: boolean }).__rttChunkHandlerInstalled = true;

  // capture:true — les erreurs resource (script/link) ne bubble pas autrement
  window.addEventListener(
    'error',
    (ev: Event) => {
      if (isStaticAssetResourceError(ev)) {
        ev.preventDefault();
        reportChunkRecovery('resource_static_asset');
        triggerChunkReload();
        return;
      }

      const event = ev as ErrorEvent;
      if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
        event.preventDefault();
        reportChunkRecovery('window_error');
        triggerChunkReload();
        return;
      }
      if (isStaleChunkHtmlScriptError(event)) {
        event.preventDefault();
        reportChunkRecovery('window_error_html_chunk');
        triggerChunkReload();
      }
    },
    true
  );

  window.addEventListener('unhandledrejection', (event) => {
    // Strict ici : le catch des loaders lazy couvre SyntaxError sans stack.
    // Évite un reload sur une SyntaxError HTML non liée aux chunks.
    if (shouldRecoverFromChunkFailure(event.reason)) {
      event.preventDefault();
      reportChunkRecovery('unhandledrejection');
      triggerChunkReload();
    }
  });

  setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
      sessionStorage.removeItem(RECOVERY_REPORT_FLAG_KEY);
    } catch {
      // ignore
    }
  }, 30_000);
};
